/* eslint-disable max-len */
import { AddSellerRequest } from '@1po/1po-bff-fe-spec/generated/backoffice/central_settings/request/AddSellerRequest';
import { CentralSettingsUpdateRequest } from '@1po/1po-bff-fe-spec/generated/backoffice/central_settings/request/CentralSettingsUpdateRequest';
import { DeleteSellerRequest } from '@1po/1po-bff-fe-spec/generated/backoffice/central_settings/request/DeleteSellerRequest';
import { UpdateSellerDataProvidersRequest } from '@1po/1po-bff-fe-spec/generated/backoffice/central_settings/request/UpdateSellerDataProvidersRequest';
import { CentralSettingsResponse } from '@1po/1po-bff-fe-spec/generated/backoffice/central_settings/response/CentralSettingsResponse';
import {
  ADD_SELLER_ERROR_RESPONSE,
  ADD_SELLER_RESPONSE,
  BACKOFFICE_GET_CENTRAL_SETTINGS_RESPONSE,
  BACKOFFICE_UPDATE_CENTRAL_SETTINGS_RESPONSE,
  DELETE_SELLER_ERROR_RESPONSE,
  DELETE_SELLER_RESPONSE,
  UPDATE_SELLER_DATA_PROVIDERS_ERROR_RESPONSE,
  UPDATE_SELLER_DATA_PROVIDERS_RESPONSE,
} from '@1po/1po-bff-fe-spec/generated/common/ResponseType';
import { QueryResponseType } from '@1po/1po-bff-fe-spec/generated/common/WsResponse';
import { all, call, put, takeEvery } from '@redux-saga/core/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { SagaIterator } from 'redux-saga';
import { WsResponse } from 'domains/webSockets/WebSocket.types';
import { notifyTop } from 'UI';
import { AppTranslation, FOUND, LOADING, sagaGuard } from 'utils';
import {
  sendAddSeller,
  sendDeleteSeller,
  sendGetCentralSettings,
  sendUpdateCentralSettings,
  sendUpdateSellerDataProviders,
} from './CentralSettings.api';
import {
  SellerCommandResponse,
  getCentralSettingsRequestSaga,
  resetDirty,
  setCentralSettings,
  setCentralSettingsStatus,
  setSellerCommandStatus,
} from './CentralSettings.store';
import {
  ADD_SELLER_REQUEST,
  DELETE_SELLER_REQUEST,
  GET_CENTRAL_SETTINGS_REQUEST,
  UPDATE_CENTRAL_SETTINGS_REQUEST,
  UPDATE_SELLER_DATA_PROVIDERS_REQUEST,
} from './CentralSettings.types';

export function* fetchCentralSettingsRequestSaga(): SagaIterator {
  yield put(setCentralSettingsStatus(LOADING));
  yield put(sendGetCentralSettings());
}

export function* fetchCentralSettingsResponseSaga(action: WsResponse<CentralSettingsResponse>): SagaIterator {
  const data = action.payload;
  yield put(setCentralSettings(data));
  yield put(setCentralSettingsStatus(FOUND));
}

export function* updateCentralSettingsRequestSaga(action: PayloadAction<CentralSettingsUpdateRequest>): SagaIterator {
  yield put(sendUpdateCentralSettings(action.payload));
}

export function* updateCentralSettingsResponseSaga(action: WsResponse<{ result: string } | null>): SagaIterator {
  const responseResult = action.payload?.result;
  const isSuccess =
    responseResult === 'SUCCESS' ||
    responseResult === 'NO_CHANGE' ||
    action.queryResponseType === QueryResponseType.COMMAND_RESPONSE_SUCCESS ||
    action.queryResponseType === QueryResponseType.COMMAND_RESPONSE_NO_CHANGE;

  if (isSuccess) {
    yield call(
      notifyTop,
      'success',
      AppTranslation.t('backoffice.central-settings.save.success', 'Central settings updated successfully'),
    );
    yield put(resetDirty());
  } else {
    yield call(
      notifyTop,
      'error',
      AppTranslation.t('backoffice.central-settings.save.error', 'Failed to update central settings'),
    );
  }
  yield put(setCentralSettingsStatus(FOUND));
}

export function* addSellerRequestSaga(action: PayloadAction<AddSellerRequest>): SagaIterator {
  yield put(sendAddSeller(action.payload));
}

export function* updateSellerDataProvidersRequestSaga(
  action: PayloadAction<UpdateSellerDataProvidersRequest>,
): SagaIterator {
  yield put(sendUpdateSellerDataProviders(action.payload));
}

export function* deleteSellerRequestSaga(action: PayloadAction<DeleteSellerRequest>): SagaIterator {
  yield put(sendDeleteSeller(action.payload));
}

type SellerResponseAction = WsResponse<SellerCommandResponse | string | null>;

function isSellerResponseSuccess(action: SellerResponseAction): boolean {
  const responseResult =
    action.payload !== null && typeof action.payload === 'object' ? action.payload.result : undefined;
  return (
    responseResult === 'SUCCESS' ||
    responseResult === 'NO_CHANGE' ||
    action.queryResponseType === QueryResponseType.COMMAND_RESPONSE_SUCCESS ||
    action.queryResponseType === QueryResponseType.COMMAND_RESPONSE_NO_CHANGE
  );
}

function getSellerErrorNotification(action: SellerResponseAction): [string, string] {
  const errorCode = typeof action.payload === 'string' ? action.payload : action.payload?.body;
  if (errorCode === 'SELLER_ALREADY_EXISTS') {
    return ['backoffice.central-settings.sellers.errors.already-exists', 'Seller already exists'];
  }
  if (errorCode === 'SELLER_NOT_FOUND') {
    return ['backoffice.central-settings.sellers.errors.not-found', 'Seller not found'];
  }
  if (errorCode === 'INVALID_SELLER_NAME') {
    return ['backoffice.central-settings.sellers.errors.invalid-name', 'Seller name is invalid'];
  }
  if (errorCode === 'INVALID_SELLER_DATA_PROVIDERS') {
    return ['backoffice.central-settings.sellers.errors.invalid-providers', 'Seller data providers are invalid'];
  }
  return ['backoffice.central-settings.sellers.errors.generic', 'Unable to update seller settings'];
}

export function* addSellerResponseSaga(action: SellerResponseAction): SagaIterator {
  if (isSellerResponseSuccess(action)) {
    yield put(setSellerCommandStatus('SUCCESS'));
    yield put(getCentralSettingsRequestSaga());
    yield call(
      notifyTop,
      'success',
      AppTranslation.t('backoffice.central-settings.sellers.toast.add-success', 'Seller added successfully'),
    );
    return;
  }
  yield put(setSellerCommandStatus('ERROR'));
  const [errorKey, errorFallback] = getSellerErrorNotification(action);
  yield call(notifyTop, 'error', AppTranslation.t(errorKey, errorFallback));
}

export function* updateSellerDataProvidersResponseSaga(action: SellerResponseAction): SagaIterator {
  if (isSellerResponseSuccess(action)) {
    yield put(setSellerCommandStatus('SUCCESS'));
    yield put(getCentralSettingsRequestSaga());
    yield call(
      notifyTop,
      'success',
      AppTranslation.t(
        'backoffice.central-settings.sellers.toast.update-success',
        'Seller data providers updated successfully',
      ),
    );
    return;
  }
  yield put(setSellerCommandStatus('ERROR'));
  const [errorKey, errorFallback] = getSellerErrorNotification(action);
  yield call(notifyTop, 'error', AppTranslation.t(errorKey, errorFallback));
}

export function* deleteSellerResponseSaga(action: SellerResponseAction): SagaIterator {
  if (isSellerResponseSuccess(action)) {
    yield put(setSellerCommandStatus('SUCCESS'));
    yield put(getCentralSettingsRequestSaga());
    yield call(
      notifyTop,
      'success',
      AppTranslation.t('backoffice.central-settings.sellers.toast.delete-success', 'Seller deleted successfully'),
    );
    return;
  }
  yield put(setSellerCommandStatus('ERROR'));
  const [errorKey, errorFallback] = getSellerErrorNotification(action);
  yield call(notifyTop, 'error', AppTranslation.t(errorKey, errorFallback));
}

export function* CentralSettingsSagas(): SagaIterator {
  yield all([
    takeEvery(GET_CENTRAL_SETTINGS_REQUEST, sagaGuard(fetchCentralSettingsRequestSaga)),
    takeEvery(BACKOFFICE_GET_CENTRAL_SETTINGS_RESPONSE, sagaGuard(fetchCentralSettingsResponseSaga)),
    takeEvery(UPDATE_CENTRAL_SETTINGS_REQUEST, sagaGuard(updateCentralSettingsRequestSaga)),
    takeEvery(BACKOFFICE_UPDATE_CENTRAL_SETTINGS_RESPONSE, sagaGuard(updateCentralSettingsResponseSaga)),
    takeEvery(ADD_SELLER_REQUEST, sagaGuard(addSellerRequestSaga)),
    takeEvery(ADD_SELLER_RESPONSE, sagaGuard(addSellerResponseSaga)),
    takeEvery(ADD_SELLER_ERROR_RESPONSE, sagaGuard(addSellerResponseSaga)),
    takeEvery(UPDATE_SELLER_DATA_PROVIDERS_REQUEST, sagaGuard(updateSellerDataProvidersRequestSaga)),
    takeEvery(UPDATE_SELLER_DATA_PROVIDERS_RESPONSE, sagaGuard(updateSellerDataProvidersResponseSaga)),
    takeEvery(UPDATE_SELLER_DATA_PROVIDERS_ERROR_RESPONSE, sagaGuard(updateSellerDataProvidersResponseSaga)),
    takeEvery(DELETE_SELLER_REQUEST, sagaGuard(deleteSellerRequestSaga)),
    takeEvery(DELETE_SELLER_RESPONSE, sagaGuard(deleteSellerResponseSaga)),
    takeEvery(DELETE_SELLER_ERROR_RESPONSE, sagaGuard(deleteSellerResponseSaga)),
  ]);
}
